import { type ActionCreator, on, type ReducerTypes } from '@ngrx/store';
import { produce } from 'immer';

import { type EngineStateBase, navMenuItemsAdapter } from '@blaze-runtime/engine-data-access';

import { isElementType } from '../../canvas-elements/element-model/element-type-guards';
import {
  DEFAULT_ITEM_LABEL,
  DEFAULT_SUBMENU_ITEM_LABEL,
  generateNewLocalStorage,
  isNavMenuSection,
} from '../../services/nav-menu-items.service';

import {
  addLocalItem,
  addSharedItem,
  changeMenuDirection,
  cleanLastAdded,
  deleteItem,
  editItemByPath,
  initItems,
  itemsLoaded,
  markItemAsDirty,
  moveItem,
  removeSection,
} from './nav-menu.actions';
import {
  addLocalMenuItemMutably,
  addSharedMenuItemMutably,
  deleteSharedMenuItemMutably,
  generateInitialItemsMutably,
  moveItemMutably,
  removeSectionMutably,
  sanitizeNavMenusAfterItemDeleteMutably,
  toSharedMenuItems,
  updateNavMenuDirectionBasedOnSectionsMutably,
} from './nav-menu.reducer.utils';

export const navMenuReducerTypes: ReducerTypes<EngineStateBase, ActionCreator[]>[] = [
  on(itemsLoaded, (state, { items }) =>
    produce(state, (draft) => {
      draft.navMenu = navMenuItemsAdapter.setMany(toSharedMenuItems(items), draft.navMenu);
    }),
  ),

  on(addLocalItem, (state, { itemType, placement }) =>
    produce(state, (draft) => {
      addLocalMenuItemMutably(draft, placement, itemType);
    }),
  ),

  on(addSharedItem, (state, { namespaceId, placement, initialData }) =>
    produce(state, (draft) => {
      addSharedMenuItemMutably(draft, namespaceId, placement, initialData);
    }),
  ),

  on(editItemByPath, (state, { id, propName, value }) =>
    produce(state, (draft) => {
      const item = draft.navMenu.entities[id];
      if (!item) {
        return;
      }

      if (item.props) {
        item.props[propName] = value;
      } else {
        item.props = { [propName]: value };
      }

      delete item.isPristine;
    }),
  ),

  on(deleteItem, (state, { id }) =>
    produce(state, (draft) => {
      const toDelete = draft.navMenu.entities[id];
      const parent = draft.navMenu.entities[toDelete?.parentId ?? ''];

      if (parent?.children) {
        parent.children = parent.children.filter((child) => child !== id);
      }

      deleteSharedMenuItemMutably(draft, id);
      sanitizeNavMenusAfterItemDeleteMutably(draft);
    }),
  ),

  on(removeSection, (state, { id, elementId }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId];
      if (!isElementType(element, 'navMenu')) {
        return;
      }

      const storage = element.props.content.storage;
      if (!storage) {
        return;
      }

      removeSectionMutably(storage, id);
    }),
  ),

  on(initItems, (state, { namespaceId, placement }) =>
    produce(state, (draft) => {
      const element = draft.entities[placement.elementId];
      if (!isElementType(element, 'navMenu')) {
        return;
      }

      const initialItems = element.props?.content?.initialItems ?? [
        { type: 'shared', props: { label: DEFAULT_ITEM_LABEL } },
        {
          type: 'shared',
          props: { label: DEFAULT_ITEM_LABEL },
          children: [
            { type: 'shared', props: { label: DEFAULT_SUBMENU_ITEM_LABEL } },
            { type: 'shared', props: { label: DEFAULT_SUBMENU_ITEM_LABEL } },
            { type: 'shared', props: { label: DEFAULT_SUBMENU_ITEM_LABEL } },
          ],
        },
      ];

      const storage = generateNewLocalStorage();
      const items = generateInitialItemsMutably(storage, initialItems, namespaceId);
      draft.navMenu = navMenuItemsAdapter.upsertMany(items, draft.navMenu);
      element.props.content.storage = storage;

      if (element.props?.content?.initialItems) {
        delete element.props.content.initialItems;
      }

      updateNavMenuDirectionBasedOnSectionsMutably(element);
    }),
  ),

  on(markItemAsDirty, (state, { id }) =>
    produce(state, (draft) => {
      const item = draft.navMenu.entities[id];
      if (item?.isPristine) {
        delete item.isPristine;
      }
    }),
  ),

  on(moveItem, (state, { elementId, dragRef, targetRef, direction }) =>
    produce(state, (draft) => {
      moveItemMutably(draft, elementId, dragRef, targetRef, direction);
    }),
  ),

  on(cleanLastAdded, (state) =>
    produce(state, (draft) => {
      draft.navMenu.lastAdded = undefined;
    }),
  ),

  on(changeMenuDirection, (state, { direction, elementId }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId];
      if (!isElementType(element, 'navMenu')) {
        return;
      }

      const storage = element.props.content.storage;
      if (direction === 'vertical' || !storage) {
        element.props.content.direction = 'vertical';
        return;
      }

      const items = Object.values(storage);
      for (const item of items) {
        if (!isNavMenuSection(item)) {
          continue;
        }

        removeSectionMutably(storage, item.id);
      }

      element.props.content.direction = direction;
    }),
  ),
];
